import React, {useContext, useEffect, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select from "@amzn/meridian/select";
import SelectOption from "@amzn/meridian/select/select-option";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import get from "lodash/get";
import {StationConfigContext} from "../../context/StationConfigContext";
import {getNodeId} from "../../Utility";
import {STRINGS} from "../../Constants";
import {getPathSchedule} from "../../resources/PathScheduleResource";
import PathScheduleList from "./PathScheduleList";

/**
 * Top-level container for the Path Schedule tab.
 *
 * Implements the per-cycle retrieval workflow (design Approach A): schedules are
 * fetched on tab load, whenever the cycle selection changes, and whenever the
 * optional date-range filter changes. GetPathSchedule is scoped to a single
 * station+cycle, so every cycle switch issues a fresh request.
 */
const PathScheduleTab = (props) => {
    const {intl} = props;
    const {state} = useContext(StationConfigContext);

    const cycleList = Array.isArray(state.cycleList) ? state.cycleList : [];

    const [selectedCycleId, setSelectedCycleId] = useState(undefined);
    const [fromDate, setFromDate] = useState(undefined);
    const [toDate, setToDate] = useState(undefined);

    const [activeSchedule, setActiveSchedule] = useState(undefined);
    const [scheduled, setScheduled] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(undefined);

    // PathSchedule keys entries by the cycle *name* (e.g., "CYCLE_1"), matching how
    // SortPathSetting/SortPathInfo and the reservation/constraints flows identify a cycle.
    // The backend partition is {stationCode}_{cycleName}, so the value sent must be
    // the cycle name, not the cycle UUID.
    const getCycleName = (stationCycle) => get(stationCycle, ["cycle", "cycleName"]);

    // Default the cycle selection to the first cycle once the cycle list is available.
    useEffect(() => {
        if (!selectedCycleId && cycleList.length > 0) {
            setSelectedCycleId(getCycleName(cycleList[0]));
        }
    }, [cycleList, selectedCycleId]);

    // Fetch whenever the selected cycle or date range changes.
    // Uses an ignore flag to guard against stale responses when the user
    // switches cycles or edits dates while a request is still in-flight.
    useEffect(() => {
        let ignore = false;
        if (!selectedCycleId) {
            return undefined;
        }
        setIsLoading(true);
        setError(undefined);
        getPathSchedule(getNodeId(), selectedCycleId, fromDate, toDate)
            .then((data) => {
                if (ignore) return;
                // Error detection keys off the errorResponse shape (errorCode), not the mere
                // presence of a `message` field — a successful GetPathScheduleOutput could carry
                // an informational message without being an error.
                if (!data || data.errorCode) {
                    setActiveSchedule(undefined);
                    setScheduled([]);
                    setError((data && data.message) ? data.message : STRINGS.RETRY_LATER);
                } else {
                    setActiveSchedule(data.activeSchedule);
                    setScheduled(Array.isArray(data.stagedSchedules) ? data.stagedSchedules : []);
                }
            }, (err) => {
                if (ignore) return;
                setActiveSchedule(undefined);
                setScheduled([]);
                setError(err || STRINGS.RETRY_LATER);
            })
            .finally(() => {
                if (!ignore) setIsLoading(false);
            });
        return () => { ignore = true; };
    }, [selectedCycleId, fromDate, toDate]);

    const onClearDateRange = () => {
        setFromDate(undefined);
        setToDate(undefined);
    };

    const renderControls = () => (
        <Row spacing={"medium"} alignmentVertical={"bottom"} wrap={"down"}>
            <Column spacing={"xsmall"}>
                <Text type="b200" color="secondary">
                    <FormattedMessage id="pathSchedule.selectCycle" defaultMessage="Cycle (CPT)"/>
                </Text>
                <Select value={selectedCycleId}
                        onChange={setSelectedCycleId}
                        placeholder={intl.formatMessage({id: "pathSchedule.selectCycle", defaultMessage: "Cycle (CPT)"})}
                        width={260}
                        size="small">
                    {cycleList.map((stationCycle) => {
                        const name = getCycleName(stationCycle);
                        return (
                            <SelectOption key={name} value={name} label={name}/>
                        );
                    })}
                </Select>
            </Column>
            <Column spacing={"xsmall"}>
                <Text type="b200" color="secondary">
                    <FormattedMessage id="pathSchedule.fromDate" defaultMessage="From date"/>
                </Text>
                <Input type="date" value={fromDate || ""}
                       onChange={(value) => setFromDate(value || undefined)}
                       size="small" width={180}/>
            </Column>
            <Column spacing={"xsmall"}>
                <Text type="b200" color="secondary">
                    <FormattedMessage id="pathSchedule.toDate" defaultMessage="To date"/>
                </Text>
                <Input type="date" value={toDate || ""}
                       onChange={(value) => setToDate(value || undefined)}
                       size="small" width={180}/>
            </Column>
            {(fromDate || toDate) &&
                <Column spacing={"xsmall"}>
                    <Button type="link" size="small" onClick={onClearDateRange}>
                        <FormattedMessage id="pathSchedule.clearDateRange" defaultMessage="Clear dates"/>
                    </Button>
                </Column>
            }
        </Row>
    );

    return (
        <Column spacing={"medium"} spacingInset={"small none"}>
            {renderControls()}
            <PathScheduleList isLoading={isLoading}
                              error={error}
                              activeSchedule={activeSchedule}
                              scheduled={scheduled}/>
        </Column>
    );
};

export default injectIntl(PathScheduleTab);
